import Service from '@ember/service';
import type { RegisterChangeset } from 'frontend/changesets/register';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import type FetchService from '@triptyk/ember-utils/services/fetch';
import type FlashMessageService from 'ember-cli-flash/services/flash-messages';
import type IntlService from 'ember-intl/services/intl';
import type FavoriteService from '../favorite';
import config from 'frontend/config/environment';
import type RouterService from '@ember/routing/router-service';

export default class RegisterChangesetService extends Service {
  @service declare store: StoreService;
  @service declare fetch: FetchService;
  @service declare flashMessages: FlashMessageService;
  @service declare intl: IntlService;
  @service declare favorite: FavoriteService;
  @service declare router: RouterService;

  async save(changeset: RegisterChangeset) {
    changeset.execute();
    const changesetData = changeset.data;

    const body = {
      kind: changesetData.kind,
      lastName: changesetData.applicant.lastName,
      firstName: changesetData.applicant.firstName,
      phone: changesetData.applicant.phone,
      email: changesetData.applicant.email,
      registerNumber: changesetData.applicant.registerNumber,
      companyName: changesetData.applicant.companyName,
      companyNumber: changesetData.applicant.companyNumber,
      institution: changesetData.applicant.institution,
      cooperationAgreement: changesetData.applicant.cooperationAgreement,
      termsAgreement: changesetData.termsAgreement,
      newsAgreement: changesetData.newsAgreement,
      // just in case a logged person tries to register. Normally, this should not happen.
      favorites: this.favorite.isLocalAdapter()
        ? await this.favorite.getFavorites()
        : [],
    };

    try {
      await this.fetch.request(`auth/register`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      await this.clearFavorites();
      this.flashMessages.success(
        this.intl.t('components.pages.register.success_message')
      );
      if (config.environment !== 'test') {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.router.transitionTo('formations.index');
      }
    } catch (e: any) {
      const message = (await e.json())?.message;
      if (this.intl.exists(`validations.backend.${message}`)) {
        return this.flashMessages.alert(
          this.intl.t(`validations.backend.${message}`)
        );
      }
      this.flashMessages.alert(
        this.intl.t('components.pages.register.fail_message')
      );
    }
  }

  private async clearFavorites() {
    if (this.favorite.isLocalAdapter()) {
      await this.favorite.clear();
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'changesets/register': RegisterChangesetService;
  }
}
